<script setup lang="ts">
import { authApi } from "@/api/authApi";
import { AuthSectionEnum } from "@/types/enums";
import { handleError } from "@/utils/errorHelper";
import { onMounted, onUnmounted, ref } from "vue";

const props = defineProps<{
  emailExists: boolean;
  emailAddress: string;
}>();

const loading = ref(false);
const error = ref("");
const countdown = ref(0);
const showResendButton = ref(false);
let countdownTimer = null;

const startCountdown = (duration) => {
  countdown.value = duration;
  countdownTimer = setInterval(() => {
    if (countdown.value > 0) {
      countdown.value--;
    } else {
      clearInterval(countdownTimer);
    }
  }, 1000);
};

const resendPasswordLink = async () => {
  loading.value = true;
  try {
    const response = await authApi.resendPasswordLink({ email: props.emailAddress });

    if (response.status == 204) {
      showResendButton.value = true;
      startCountdown(60);
    }
  } catch (responseError) {
    error.value = handleError(responseError);
  } finally {
    loading.value = false;
  }
};

const submit = async () => {
  loading.value = true;
  try {
    const result = await authApi.forgot({ email: props.emailAddress });
    if (result.status == 204) {
      showResendButton.value = true;
      startCountdown(60);
    }
  } catch (responseError) {
    error.value = handleError(responseError);
  } finally {
    loading.value = false;
  }
};

const resetModal = () => {
  emit("update:activeSection", AuthSectionEnum.Email);
  emit("update:emailExists", false);
  emit("update:header", "Welcome to 4C Predictions");
};

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "update:activeSection", value: AuthSectionEnum): void;
  (e: "update:emailExists", value: boolean): void;
  (e: "update:header", value: string): void;
}>();

onMounted(() => startCountdown);

onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer);
  }
});
</script>

<template>
  <div class="flex items-center justify-between">
    <Button
      @click="resetModal"
      type="button"
      class="group border-transparent bg-transparent !p-0"
      aria-label="Go back to email input"
    >
      <i class="pi pi-arrow-left text-sm text-slate-500 group-hover:text-black"></i>
      <span class="text-[15px] font-medium text-slate-500 group-hover:text-black">Back</span>
    </Button>
    <span>{{ emailAddress }}</span>
  </div>
  <div class="mb-5 mt-5 border-y py-5 text-center">
    <p
      v-if="showResendButton"
      class="flex w-full flex-col items-center justify-center text-sm text-gray-600"
    >
      <span class="font-semibold text-green-500"
        >We've sent a password reset link to your email.</span
      >
      <span>Didn't get it? Make sure to check your spam folder!</span>
    </p>
    <p v-else class="flex w-full flex-col items-center justify-center text-sm text-gray-600">
      <span class="font-semibold text-black"
        >Instructions will be sent to your registered email address.</span
      >
    </p>
    <Button
      v-if="showResendButton"
      class="mt-4 w-full max-w-[310px] !px-3"
      :disabled="countdown > 0"
      @click="resendPasswordLink"
      type="button"
    >
      <i v-if="loading" class="pi pi-spin pi-spinner"></i>
      <div v-if="countdown > 0">
        <span>Resend available in: </span>
        <Button
          text
          type="button"
          class="w-[30px] bg-black !p-0 !text-sm font-bold !text-primary"
          >{{ countdown }}</Button
        >
      </div>
      <span v-else>Resend Link</span>
    </Button>
    <Button
      v-else
      class="mt-4 w-full max-w-[310px] !px-3"
      :disabled="loading"
      @click="submit"
      type="button"
    >
      <i v-if="loading" class="pi pi-spin pi-spinner"></i>
      <span>Send Instructions</span>
    </Button>
  </div>
</template>
